/**
 * Confirmation Log — per-booking passenger-confirmation delivery outcome.
 *
 * Read-only view of the `booking_confirmations` collection: for each booking it
 * shows the single resolved outcome (SMS / WhatsApp), the current state, the
 * channel attempts and any Twilio error (e.g. 63016/63051). Backs the idempotent
 * "one booking = one confirmation" guarantee with visibility for dispatchers.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  RefreshCw, CheckCircle2, Clock, Send, XCircle, MessageSquare,
  Smartphone, AlertTriangle,
} from "lucide-react";

const STATE_META = {
  succeeded: { label: "Delivered", cls: "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]", icon: CheckCircle2 },
  sent:      { label: "Sent · awaiting receipt", cls: "bg-[#DBEAFE] text-[#1E40AF] border-[#93C5FD]", icon: Send },
  pending:   { label: "Pending", cls: "bg-[#F3F4F6] text-[#374151] border-[#D1D5DB]", icon: Clock },
  failed:    { label: "Failed", cls: "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]", icon: XCircle },
};

const ChannelBadge = ({ channel }) => {
  if (!channel) return <span className="text-muted-foreground text-xs">—</span>;
  const isWa = channel === "whatsapp";
  const Icon = isWa ? MessageSquare : Smartphone;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${isWa ? "text-[#128C7E]" : "text-[#0A0A0A]"}`}>
      <Icon className="w-3.5 h-3.5" /> {isWa ? "WhatsApp" : "SMS"}
    </span>
  );
};

export function ConfirmationLogPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/admin/confirmations?limit=200");
      setRows(data.confirmations || []);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="confirmation-log-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold">Confirmation Log</h2>
          <p className="text-sm text-muted-foreground">
            Per-booking passenger confirmation — one outcome per booking, across SMS &amp; WhatsApp.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="confirmation-log-refresh">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FCA5A5] rounded-lg px-3 py-2" data-testid="confirmation-log-error">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="text-sm text-muted-foreground border border-dashed rounded-lg px-4 py-10 text-center" data-testid="confirmation-log-empty">
          No booking confirmations yet. New bookings will appear here with their delivery outcome.
        </div>
      )}

      <div className="space-y-2">
        {rows.map((r) => {
          const meta = STATE_META[r.state] || STATE_META.pending;
          const Icon = meta.icon;
          const isOpen = expanded === r.ride_id;
          return (
            <div key={r.ride_id} className="border border-border rounded-lg bg-white" data-testid={`confirmation-row-${r.ride_id}`}>
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : r.ride_id)}
                className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-secondary/50 transition-colors"
              >
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-bold ${meta.cls}`}>
                  <Icon className="w-3.5 h-3.5" /> {meta.label}
                </span>
                <span className="font-mono text-xs text-muted-foreground truncate">{r.ride_id}</span>
                <span className="ml-auto flex items-center gap-4">
                  <span className="text-xs text-muted-foreground">{r.phone}</span>
                  <ChannelBadge channel={r.outcome_channel} />
                </span>
              </button>

              {isOpen && (
                <div className="px-4 pb-3 pt-1 border-t border-border/60 space-y-2" data-testid={`confirmation-detail-${r.ride_id}`}>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><span className="text-muted-foreground">Outcome channel:</span> <ChannelBadge channel={r.outcome_channel} /></div>
                    <div><span className="text-muted-foreground">WhatsApp status:</span> {r.whatsapp_status || "—"}{r.whatsapp_error_code ? ` (err ${r.whatsapp_error_code})` : ""}</div>
                    <div><span className="text-muted-foreground">SMS status:</span> {r.sms_status || "—"}</div>
                    <div><span className="text-muted-foreground">Created:</span> {r.created_at?.slice(0, 19).replace("T", " ") || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">Attempts</div>
                    <div className="space-y-1">
                      {(r.attempts || []).map((a, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs">
                          <ChannelBadge channel={a.channel} />
                          <span className="font-medium">{a.status}</span>
                          {a.error_code && <span className="text-[#991B1B]">err {a.error_code}</span>}
                          <span className="text-muted-foreground ml-auto">{a.ts?.slice(11, 19)}</span>
                        </div>
                      ))}
                      {(r.attempts || []).length === 0 && <span className="text-xs text-muted-foreground">No attempts recorded.</span>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ConfirmationLogPanel;
